<template>
  <div>
    <div class="comments-wrapper">
      <div class="comments-button" @click="closeComments">
        <span>{{ $t('GeneralTranslations.Comments') }}</span>
        <v-icon class="pl-1" size="12px" color="white">mdi-triangle-down</v-icon>
      </div>
      <v-card class="chat-box">
        <v-row class="pt-4 px-4" justify="space-around d-flex flex-column">

          <v-list class="v-list-scrollable" ref="scrollableList">
            <template v-for="(message) in comments">
              <v-list-item v-if="message.senderId != user.id" :key="message.createdAt">
                <v-list-item-avatar class="align-self-start mr-2">
                  <v-avatar size="40">
                    <v-img :src="message.senderPhotoUrl"></v-img>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content class="received-message">
                  <v-card color="success" class="flex-none" style="box-shadow: none !important">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-caption">{{ message.senderName }} </span>
                      <span class="align-self-start text-subtitle-1">{{ message.text }}</span>
                      <span class="text-caption font-italic align-self-end">{{ message.createdAtDisplay }}</span>
                    </v-card-text>
                  </v-card>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-else :key="message.createdAt">
                <v-list-item-content class="sent-message justify-end">
                  <v-card color="primary" class="flex-none no-shadow" style="box-shadow: none !important">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-subtitle-1 chat-message">{{ message.text }}</span>
                      <span class="text-caption font-italic align-self-start">{{ message.createdAtDisplay }}</span>
                    </v-card-text>
                  </v-card>
                </v-list-item-content>
                <v-list-item-avatar class="align-self-start ml-2">
                  <v-img :src="user.profilePicUrl"></v-img>
                </v-list-item-avatar>
              </v-list-item>
            </template>
          </v-list>
        </v-row>
        <div class="comments-form p-4">
          <v-textarea v-model="message" v-on:keyup.enter="sendComment" :label="'Enter your message here to reply'"
            rows="1"></v-textarea>
          <v-btn width="100px" :disabled="isMessageInvalid" class="btn-default inline-block float-right"
            @click="sendComment">
            {{ $t('GeneralTranslations.Send') }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from "moment/moment";
import * as firebase from "firebase/app";
import { mapGetters } from "vuex";
import { CommentSenderRoleEnum } from '@/enums/CommentSenderRoleEnum';

export default {
  name: "Comments",
  props: {
    data: Object
  },
  data() {
    return {
      messagesLoaded: true,
      comments: [],
      message: null,
      onCommentsUnsubscribe: null,
      isChatActive: false,
    }
  },

  computed: {
    facility() {
      return this.$store.state.authModule.currentFacilityData
    },
    dateTimeFormat() {
      return `${this.facility.dateFormat} ${this.facility.timeFormat}`
    },
    isMessageInvalid(){
      return !this.message || this.message.trim() === "";
    },
    ...mapGetters({
      user: "user"
    })
  },

  async created() {
    await this.fetchComments()
    await this.$store.dispatch("resetServiceRequestCommentsCount", this.data.serviceRequestId)
    await this.subscribeOnComments()
    this.$refs.bottomElement?.scrollIntoView();
  },

  beforeDestroy() {
    this.unsubscribeFromComments();
  },

  methods: {
    closeComments() {
      this.$emit("close");
    },

    async sendComment() {

      if(this.isMessageInvalid) return

      const familyMemberId = this.comments.find(x => x.senderRole !== CommentSenderRoleEnum.Staff)?.senderId || this.data.bookByFamilyId

      if(!familyMemberId){
        this.$toast.open({ message: this.$t('Comments.RecipientNotFound'), type: "error"})
        return
      }

      const comment = {
        residentId: this.data.residentId,
        serviceName: this.data.serviceName,
        serviceItemType: this.data.serviceItemType,
        senderId: this.user.id,
        senderPhotoUrl: this.user.profilePicUrl,
        senderName: this.user.name,
        senderRole: CommentSenderRoleEnum.Staff,
        receiverId: familyMemberId,
        serviceRequestId: this.data.serviceRequestId,
        read: false,
        readDateTime: null,
        text: this.message,
        facilityId: this.facility.id,
      }

      this.message = ''

      await this.$store.dispatch("addComment", comment);
    },

    async fetchComments() {
      this.comments = this.decorateComments(await this.$store.dispatch("getCommentsByServiceRequestId", this.data.serviceRequestId));
      this.scrollToLastItem()
      this.readIncomingComments(this.data.serviceRequestId)
      console.log(`fetched ${this.comments} comments`)
    },

    decorateComments(comments) {
      const items = [...comments].map((c) => {
        const comment = c
        const dateTime = moment(comment.createdAt.seconds * 1000)
        const isTodayComment = dateTime.isSame(moment(), 'day')
        comment.createdAtDisplay = isTodayComment ? moment(dateTime).format(this.facility.timeFormat) : moment(dateTime).format(this.dateTimeFormat)
        return comment
      })

      return items
    },

    async editComment({ messageId, newContent, roomId, files }) {
      console.log('editMessage')
    },

    async deleteComment({ message, roomId }) {
      console.log('deleteMessage')
    },

    async readIncomingComments(serviceRequestId) {
      const result = await this.$store.dispatch("readIncomingComments", serviceRequestId)
      if (result === true) {
        this.comments.forEach(item => {
          if (item.senderRole !== CommentSenderRoleEnum.Staff) {
            item.read = true
          }
        });
        await this.$store.dispatch("resetServiceRequestCommentsCount", this.data.serviceRequestId)
      }
    },

    async readIncomingComment(comment) {
      if (comment.senderRole === CommentSenderRoleEnum.Staff) return

      const result = await this.$store.dispatch("readIncomingComment", comment.id)
      if (result === true) {
        const item = this.comments.find(x => x.id === comment.id);
        item.read = true
      }
    },

    async subscribeOnComments() {
      console.log('subscribeOnComments')

      const query = firebase.firestore().collection("comments")
        .where("facilityId", "==", this.facility.id)
        .where("serviceRequestId", "==", this.data.serviceRequestId)
        // request data that was created after start listening
        .where("createdAt", ">=", firebase.firestore.Timestamp.fromMillis(Date.now()))
        .orderBy("createdAt", "desc")

      this.onCommentsUnsubscribe = await query
        .onSnapshot((snapshot) => {
          snapshot.docChanges().forEach(async (change) => {

            if (change.type === "added") {
              console.log(`received new comment`)
              const comment = this.decorateComments([{ id: change.doc.id, ...change.doc.data() }])[0]
              this.comments.push(comment)
              this.scrollToLastItem()
              this.readIncomingComment(comment)
            }

            if (change.type === "removed") {
              console.log(`interlocutor removed a comment`)
              this.comments = this.comments.filter(x => x.id !== change.doc.id)
            }
          })
        })
    },

    unsubscribeFromComments() {
      console.log('unsubscribeFromComments')
      if (this.onCommentsUnsubscribe) {
        this.onCommentsUnsubscribe()
      }
    },

    scrollToLastItem() {
      setTimeout(() => {
        const items = document.querySelectorAll('.v-list-item');
        const count = items.length
        if (count > 1) {
          const item = items[count - 1];
          item.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500)
    }
  }
}
</script>

<style scoped lang="scss">
.v-card {
  padding: initial !important;
  min-height: initial !important;
  background-color: #fff !important;
}

.comments-form {
  position: absolute;
  bottom: 0;
  width: -webkit-fill-available;
}

.no-shadow {
  box-shadow: none !important;
}

.chat-message {
  display: unset !important;
  white-space: break-spaces;
}

.chat-screen {
  max-height: 320px;
  overflow-y: auto;
}

.flex-none {
  flex: unset;
}

.received-message,
.sent-message {
  line-break: anywhere;
  cursor: text;
}

.received-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: 54px;
  right: auto;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #4caf50 transparent transparent transparent;
}

.sent-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: auto;
  right: 54px;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #3f51b5 transparent transparent transparent;
}

.comments-button {
  position: fixed;
  bottom: 750px;
  right: 430px;
  width: 140px;
  height: 50px;
  background-color: #3f51b5;
  transform-origin: center top;
  transform: rotate(-90deg);
  color: white;
  text-align: center;
  line-height: 50px;
  transition: right 0.25s;
  cursor: pointer;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  outline: none;
}

@media screen and (max-width: 800px) {
  .comments-button {
    position: static;
    width: 100%;
    max-width: 350px;
    margin: auto;
    height: 50px;
    transform: rotate(0deg);
  }
}

.chat-box {
  width: 450px;
  height: 870px;
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1;
}

.chat-content {
  list-style: none;
  padding: 0;
  color: #fff;
}

.v-list-scrollable {
  height: auto;
  max-height: 740px;
  overflow-y: auto
}

.v-list-scrollable::-webkit-scrollbar {
  width: 15px;
}

.v-list-scrollable::-webkit-scrollbar-track {
  background: #e6e6e6;
  border-left: 1px solid #dadada;
}

.v-list-scrollable::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border: solid 3px #e6e6e6;
  border-radius: 7px;
}

.v-list-scrollable::-webkit-scrollbar-thumb:hover {
  background: rgb(117, 117, 163);
}
</style>
